import { CACHE, idb, OUTBOX } from './idb.js';
import type { Op, QueuedOp } from './ops.js';

/** The last good answer the server gave, per request, plus the queue of writes
 *  waiting to reach it. Reads and writes are deliberately separate: the cache is
 *  disposable and can be thrown away at any time, the queue is the only copy of
 *  something the user has already been told is saved. */

interface Entry<T> { value: T; at: number }

export async function readCache<T>(key: string): Promise<{ value: T; at: number } | null> {
  const e = await idb.get<Entry<T>>(CACHE, key);
  return e ? { value: e.value, at: e.at } : null;
}

export async function writeCache<T>(key: string, value: T): Promise<void> {
  await idb.put<Entry<T>>(CACHE, { value, at: Date.now() }, key);
}

/** Signing out has to leave nothing behind: the next person to open the app on
 *  this device is not necessarily the same person. The queue goes too — its
 *  writes are attributed to a session that no longer exists. */
export async function clearAll(): Promise<void> {
  await Promise.all([idb.clear(CACHE), idb.clear(OUTBOX)]);
}

export async function enqueue(op: Op, label: string): Promise<void> {
  await idb.put<Omit<QueuedOp, 'seq'>>(OUTBOX, { at: Date.now(), op, label });
}

export async function pending(): Promise<QueuedOp[]> {
  const all = (await idb.all<QueuedOp>(OUTBOX)) ?? [];
  return all.sort((a, b) => a.seq - b.seq);
}

export async function dequeue(seq: number): Promise<void> {
  await idb.del(OUTBOX, seq);
}
